import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { InviteSubject } from "@dentacore/shared-types";

import { ApiError, inviteSubject } from "../api/client";
import { useAuth } from "../auth/auth-context";
import { RequiredMark } from "../ui/required-mark";

/**
 * The page an invite link opens — STORY-064.
 *
 * **The only screen in the product a stranger can reach**, which is what shapes
 * it: it asks for one thing, says nothing it was not asked to say, and every
 * failure resolves to the same sentence the server chose. It does not offer a
 * way to request a new link, because there is no unauthenticated path to one —
 * an admin issues it, and telling the reader that is more use than a button
 * that cannot work.
 *
 * Unstyled beyond the minimum, exactly as `login-page.tsx` is and for the same
 * reason: this sits outside the shell, and the mockup has no reference for it.
 */
export function AcceptInvitePage(): React.JSX.Element {
  const { token } = useParams<{ token: string }>();
  const { status, acceptInvite, signOut } = useAuth();
  const navigate = useNavigate();

  const [subject, setSubject] = useState<InviteSubject | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [endedASession, setEndedASession] = useState(false);

  /**
   * Whether this browser was already holding somebody's session when the link
   * was opened — decided once, before anything on this page has run.
   *
   * **It cannot be read off `status`**, which is the trap the first attempt
   * fell into: accepting the invite signs the newcomer in, so `status` becomes
   * `signed-in` again a moment later and a rule phrased as *"sign out whoever
   * is signed in"* promptly signed out the very person it had just let in.
   * They landed back on the product chooser with a working password and no
   * session.
   */
  const sessionBefore = useRef<"unknown" | "none" | "someone">("unknown");
  const alreadyEndedOne = useRef(false);

  useEffect(() => {
    if (token === undefined) return;
    const controller = new AbortController();

    inviteSubject(token)
      .then((found) => {
        if (!controller.signal.aborted) setSubject(found);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        // The server's own sentence, verbatim: it distinguishes expired (410)
        // from unusable (404) deliberately, and rewording would lose that.
        setLinkError(caught instanceof ApiError ? caught.message : "Could not reach the server.");
      });

    return () => controller.abort();
  }, [token]);

  /**
   * **A live link ends whatever session this browser is holding.**
   *
   * The case that made this urgent, reported from real use: an admin issues a
   * link and hands it to the person it is for — often on a machine the admin
   * has already signed in on. The accept screen itself was always correct, but
   * the session underneath it was not, so the newcomer's very next click landed
   * them inside the admin's account. *"i cannot send someone a link that logs
   * him into my account."*
   *
   * **Only once the link resolves**, which is what keeps the branch below
   * working: a spent or forged link sets `linkError` and never gets here, so
   * somebody following their own stale link is still bounced to their
   * dashboard rather than signed out of it.
   *
   * The holder of a valid unaccepted invite is never the signed-in user —
   * their account is inactive until this page finishes — so there is no case
   * where this signs out the person it is for.
   */
  // Recorded the first time the session is known either way, and never again.
  // `restoring` is not an answer, so it is waited out rather than read.
  useEffect(() => {
    if (sessionBefore.current !== "unknown" || status === "restoring") return;
    sessionBefore.current = status === "signed-in" ? "someone" : "none";
  }, [status]);

  useEffect(() => {
    if (alreadyEndedOne.current || subject === null) return;
    if (sessionBefore.current !== "someone") return;

    alreadyEndedOne.current = true;
    setEndedASession(true);
    void signOut();
    // `status` is a dependency because the answer above may not have been
    // known yet on the pass where `subject` arrived.
  }, [subject, status, signOut]);

  /**
   * **A refused link says so. It never hands the reader somebody else's
   * session** (QA20-6, 2026-09-20).
   *
   * This used to be `<Navigate to="/" replace />` whenever a link was rejected
   * *and* a session existed, on the reasoning that somebody signed in following
   * an old link should be taken to the app rather than offered a password they
   * do not need. That reasoning assumed the signed-in person **is** the
   * invitee — which is the one case that cannot happen, because an invitee's
   * account is inactive until this page finishes.
   *
   * So the person it actually fired for was the *inviter*: an admin issues a
   * link, opens it to check, and the visitor beside them lands in the admin's
   * own dashboard with the URL rewritten to `/` and nothing said. Reported
   * verbatim as *"i pasted the address in a new tab, i got immediately signed
   * in to the account that issued the invite."*
   *
   * QA20-5 removed the common way to get here — links now carry the clinic's
   * own subdomain instead of whatever origin the issuer was on — but spent,
   * revoked and expired links still arrive, and each of them has to be readable
   * rather than swallowed. The refusal below already says which.
   */

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    // Checked here and nowhere else: a confirmation field is a typing aid, not
    // a rule, so the server never hears about it. Skipped entirely when there
    // is no password to confirm.
    if (subject?.needsPassword === true && password !== confirm) {
      setError("Those two passwords are not the same.");
      return;
    }

    setSubmitting(true);
    try {
      // Undefined, not an empty string: the server refuses an acceptance that
      // needs a password and arrives without one, and "" would read as a
      // password that fails the policy rather than as none at all.
      await acceptInvite(token ?? "", subject?.needsPassword === true ? password : undefined);
      await navigate("/", { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not reach the server.");
    } finally {
      setSubmitting(false);
    }
  }

  if (linkError !== null) {
    return (
      <main className="sheet-wide">
        <h1>This link cannot be used</h1>
        <p role="alert" data-testid="invite-link-error">
          {linkError}
        </p>
        <p>
          Invite links are single use and expire after a week. Ask whoever added you to the clinic
          for a new one — issuing it replaces this link.
        </p>
        {/*
          **Offered, not taken.** Somebody is signed in on this browser and it
          is very often the admin who issued the link, checking it — so the way
          back to their own dashboard belongs here as a link they choose to
          follow. Following it *for* them is the bug this screen was fixed for
          (QA20-6): a visitor standing at the same machine would have been
          walked into that account without being told.
        */}
        {status === "signed-in" && (
          <p>
            <Link to="/">Back to DentaCore</Link> — you are signed in on this browser.
          </p>
        )}
      </main>
    );
  }

  if (subject === null) {
    return (
      <main className="sheet-wide">
        <p>Checking that link&hellip;</p>
      </main>
    );
  }

  /**
   * **Somebody who already has an account is joining, not registering** (I2).
   *
   * A doctor at two practices has one password. Asking them to choose a second
   * would leave them with two to tell apart, and the server discards it anyway
   * — so the screen asks them to confirm instead, and sends nothing.
   */
  const joining = !subject.needsPassword;

  return (
    <main className="sheet-wide">
      <h1>{joining ? `Join ${subject.clinicName}` : "Set your password"}</h1>
      <p data-testid="invite-greeting">
        {joining
          ? `${subject.firstName}, you have been added to ${subject.clinicName}. You already have a
             DentaCore account, so there is nothing to set up — confirm and you will be signed in
             with the password you already use.`
          : `${subject.firstName}, you have been added to ${subject.clinicName}. Choose a password
             and you will be signed in.`}
      </p>
      <p>
        Signing in as <strong>{subject.email}</strong>
      </p>

      {endedASession && (
        <p data-testid="invite-signed-out">
          Somebody else was signed in on this browser, so they have been signed out. This link only
          ever signs in the person it names.
        </p>
      )}

      <form onSubmit={(event) => void handleSubmit(event)}>
        {!joining && (
          <>
            <label htmlFor="invite-password">
              Password
              <RequiredMark />
            </label>
            <input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              data-testid="invite-password"
            />

            <label htmlFor="invite-confirm">
              Password again
              <RequiredMark />
            </label>
            <input
              id="invite-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              data-testid="invite-confirm"
            />
          </>
        )}

        {error !== null && (
          <p role="alert" data-testid="invite-error">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting} data-testid="invite-submit">
          {submitting
            ? joining
              ? "Joining…"
              : "Setting it…"
            : joining
              ? "Join and sign in"
              : "Set password and sign in"}
        </button>
      </form>
    </main>
  );
}
